import { AnyAction } from 'redux';
import { ILoadingError } from '#/types/global';
import { IArtistResponse } from '#/types/API/artists';
import { IAlbumResponse, Item, Tracks } from '#/types/API/albums';
import {
  createActionArtistAlbumsAddDetails,
  createActionArtistAlbumsReset,
  createActionArtistAlbumsSetErrorDetails,
  createActionArtistAlbumsSetIsFollowed,
  createActionArtistAlbumsSetLayout,
  createActionArtistAlbumsSetSelectedAlbumType,
  createActionArtistAlbumsSetTrackIsFollowed,
  createActionArtistAlbumsSetTracks,
  createActionArtistAlbumsStartLoading,
  createActionArtistAlbumsStopLoading,
} from '../actions/artistAlbums';

interface IItem extends Item {
  favorite?: boolean;
}

export interface ITrack extends Tracks {
  items: IItem[];
}

export interface IAlbums extends IAlbumResponse {
  favorite?: boolean;
  tracks: ITrack;
}

export type TSelectedAlbumType = '' | 'album' | 'single' | 'compilation';

export type TLayout = 'detailed' | 'grid';

export interface IArtistAlbums {
  details?: IArtistResponse;
  albums?: IAlbums[];
  selectedAlbumType?: TSelectedAlbumType;
  layout: TLayout;
}

type TState = IArtistAlbums & ILoadingError;

// eslint-disable-next-line unicorn/no-null
const initialState: TState = {
  _loading: true,
  _error: false,
  layout: 'detailed',
};

const artistAlbums = (
  state: TState = initialState,
  action: AnyAction
): TState => {
  if (createActionArtistAlbumsAddDetails.doesMatch(action)) {
    return { ...state, ...action.payload };
  }

  if (createActionArtistAlbumsStartLoading.doesMatch(action)) {
    return { ...state, _loading: true };
  }

  if (createActionArtistAlbumsStopLoading.doesMatch(action)) {
    return { ...state, _loading: false };
  }

  if (createActionArtistAlbumsSetErrorDetails.doesMatch(action)) {
    return { ...state, _error: action.payload };
  }

  if (createActionArtistAlbumsSetSelectedAlbumType.doesMatch(action)) {
    return { ...state, selectedAlbumType: action.payload };
  }

  if (createActionArtistAlbumsSetLayout.doesMatch(action)) {
    return { ...state, layout: action.payload };
  }

  if (createActionArtistAlbumsSetIsFollowed.doesMatch(action)) {
    if (state.albums) {
      const newAlbums = [...state.albums];

      const index = state.albums.findIndex(
        (album) => album.id === action.payload.album
      );

      if (index !== -1) {
        newAlbums[index].favorite = action.payload.follow;

        return { ...state, albums: newAlbums };
      }
    }
    return state;
  }

  if (createActionArtistAlbumsSetTrackIsFollowed.doesMatch(action)) {
    if (state.albums) {
      const newAlbums = [...state.albums];

      const indexAlbum = state.albums.findIndex(
        (album) => album.id === action.payload.album
      );

      if (indexAlbum !== -1) {
        const indexTrack = newAlbums[indexAlbum].tracks.items.findIndex(
          (track) => track.id === action.payload.track
        );
        if (indexTrack !== -1) {
          newAlbums[indexAlbum].tracks.items[indexTrack].favorite =
            action.payload.follow;
          return { ...state, albums: newAlbums };
        }
      }
    }
    return state;
  }

  if (createActionArtistAlbumsSetTracks.doesMatch(action)) {
    if (state.albums) {
      const newAlbums = [...state.albums];

      const index = state.albums.findIndex(
        (album) => album.id === action.payload.album
      );

      if (index !== -1) {
        newAlbums[index].tracks = action.payload.tracks;

        return { ...state, albums: newAlbums };
      }
    }
    return state;
  }

  if (createActionArtistAlbumsReset.doesMatch(action)) {
    return initialState;
  }

  return state;
};

export default artistAlbums;
